import React, { useContext } from "react";
import PropTypes from "prop-types";
import { Box, Grid, Typography, Link, Chip } from "@material-ui/core/";

import ClearIcon from "@material-ui/icons/Clear";
import classes from "./Filters.module.css";

import JDPLifeHelpIcon from "../../UI/Icons/JDPLifeHelpIcon";
import { useSearchParams } from "../../../hooks/useSearchParams";
import ConfigContext from "../../../context/ConfigContext";

import { setLocalStorageWithFilter } from "../../../utils/LocalStorageUtils";
import { FAQ_HOW_TO_FILTER } from "../../FAQ/faqData";

const FilterFooter = (props) => {
  const { honeycomb, applyFilter } = props;
  const { organismFilters, fileFilters, goldFilters } =
    useContext(ConfigContext);

  let filterConfig = organismFilters
    ? organismFilters.concat(fileFilters)
    : fileFilters;

  if (filterConfig && goldFilters) {
    filterConfig = filterConfig.concat(goldFilters);
  }

  const { searchParams } = useSearchParams();

  /** Delete the key, value filter pair from the URL */
  const handleDelete = (key, value) => {
    if (!searchParams.has(key)) return;

    const filterParams = searchParams.get(key).split(",");

    const index = filterParams.indexOf(value);
    if (index > -1 && filterParams.length > 1) {
      // Remove value from list of values for this filter
      filterParams.splice(index, 1);
      searchParams.set(key, filterParams);
    } else if (index > -1) {
      // Only 1 filter value, remove whole filter
      searchParams.delete(key);
    }

    // Send delete to HC
    honeycomb.sendUiInteractionSpan(`filter-remove-option`, {
      filter: {
        field: key,
        value,
        "updated-value": searchParams.has(key)
          ? searchParams.get(key).toString().split(",")
          : [],
      },
    });

    // update URL
    const queryString = searchParams.toString();
    applyFilter(`?${queryString}`);
  };

  // if config has values prefer title as label
  const getChipLabel = (paramKey, paramValue) => {
    const config = filterConfig.find((el) => el.filterId === paramKey);
    const label = config.labeler ? config.labeler(paramValue) : paramValue;

    return label;
  };

  /** Build filter chips based on params in URL */
  const getFilterChips = () => {
    const chips = [];
    const paramEntries = searchParams.entries();
    let searchParam = paramEntries.next();
    while (!searchParam.done) {
      const paramKey = searchParam.value[0];
      const paramValues = searchParam.value[1].split(",");

      if (filterConfig.some((config) => config.filterId === paramKey)) {
        paramValues.forEach((paramValue) => {
          const chip = (
            <Chip
              variant="outlined"
              onDelete={() => {
                handleDelete(paramKey, paramValue);
              }}
              deleteIcon={<ClearIcon className={classes.deleteIcon} />}
              label={getChipLabel(paramKey, decodeURIComponent(paramValue))}
              className={classes.filterChip}
              id={paramKey + paramValue}
              key={paramKey + paramValue}
            />
          );

          chips.push(chip);
        });
      }
      searchParam = paramEntries.next();
    }

    setLocalStorageWithFilter(chips.length);
    return chips;
  };

  /** Remove all filter params from URL */
  const handleClearAll = () => {
    honeycomb.sendUiInteractionSpan(`filter-clear-all`, {
      filter: { "updated-value": [] },
    });

    // Get keys of only 'filter' params (dont want to delete expand, organism, ect)
    const deleteKeys = [];
    searchParams.forEach((value, key) => {
      if (filterConfig.some((config) => config.filterId === key)) {
        deleteKeys.push(key);
      }
    });

    // Delete all filters by key
    deleteKeys.map((key) => searchParams.delete(key));

    // update URL
    const queryString = searchParams.toString();
    applyFilter(`?${queryString}`);
  };

  /** Get the clear all button if filter params present in URL */
  const getClearAll = () => {
    let haveFilters = false;

    // Check if any filters are shown currently
    const paramEntries = searchParams.entries();
    let searchParam = paramEntries.next();
    while (!searchParam.done && !haveFilters) {
      const paramKey = searchParam.value[0];
      if (filterConfig.some((config) => config.filterId === paramKey)) {
        haveFilters = true;
      }
      searchParam = paramEntries.next();
    }

    // Dont show clear button when no filters present
    if (!haveFilters) return;

    return (
      <Box className={classes.clearAllBox}>
        <Typography
          className={`${classes.bodySmall} ${classes.clearAllText}`}
          data-testid="clearAllFilters"
          onClick={handleClearAll}
        >
          Clear All
        </Typography>
      </Box>
    );
  };

  return (
    <Box className={`${classes.rootBox} ${classes.filterFooter}`}>
      <Grid container direction="row" justify="space-between" wrap="nowrap">
        <Box className={classes.chipContainer} data-testid="chip-container">
          <Box className={classes.filterChipBox}>
            {getFilterChips()}
            {getClearAll()}
          </Box>
        </Box>
        <Box className={classes.filterHelpBox}>
          <JDPLifeHelpIcon>
            <Typography
              className={`${classes.bodySmall} ${classes.filterHelpText}`}
            >
              <Link
                className={classes.filterHelpLink}
                href={FAQ_HOW_TO_FILTER}
                target="_blank"
                data-testid="filterHelp"
                onClick={() => {
                  honeycomb.sendUiInteractionSpan(
                    "clicked-internal-link: Learn more about filters"
                  );
                }}
              >
                Learn more
              </Link>{" "}
              about filters
            </Typography>
          </JDPLifeHelpIcon>
        </Box>
      </Grid>
    </Box>
  );
};

export default FilterFooter;

FilterFooter.defaultProps = {
  honeycomb: { sendUiInteractionSpan: () => {} },
};

FilterFooter.propTypes = {
  honeycomb: PropTypes.shape({
    sendUiInteractionSpan: PropTypes.func,
  }),
  applyFilter: PropTypes.func.isRequired,
};
